import { useState } from 'react';
import { ApiError, api } from '../../api';
import type { GoogleStatus } from '../../types';
import { openDrivePicker, type PickerTarget } from '../../lib/googlePicker';

const MAX_ITEMS = 100;

function DriveGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M8.5 3.5h7l6 10.5-3.5 6h-12L2.5 14z" strokeLinejoin="round" />
      <path d="M8.5 3.5L15 14.5h6.5M15.5 3.5L9 14.5 5.5 20.5" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Google Drive import. Memoryflix asks only for the files the user chooses
 * (drive.file scope); a pasted link opens the same chooser on that file or
 * folder, because a link alone never grants access.
 */
export default function DriveImport({
  status,
  onStatusChange,
  onImported,
}: {
  status: GoogleStatus | null;
  onStatusChange: () => void;
  onImported: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [link, setLink] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const connect = () => {
    // A full-page navigation: Google's consent screen must not be framed.
    window.location.assign('/api/google/oauth/start?returnTo=/import');
  };

  const pick = async (target: PickerTarget) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const token = await api.googlePickerToken();
      const docs = await openDrivePicker({ ...token, maxItems: MAX_ITEMS, target });
      if (docs.length === 0) {
        setNotice('Nothing was selected.');
        return;
      }
      const res = await api.googleImport(
        docs.map((d) => ({ id: d.id, resourceKey: d.resourceKey, name: d.name })),
      );
      setNotice(`Importing ${res.count} ${res.count === 1 ? 'file' : 'files'} from Google Drive.`);
      setLink('');
      onImported();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'GOOGLE_RECONNECT_REQUIRED') {
        setError(err.message);
        onStatusChange();
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const openLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!link.trim()) return;
    setError(null);
    setNotice(null);
    try {
      const info = await api.inspectDriveLink(link);
      if (info.kind === 'file' && info.id) {
        if (!status?.connected) return setError('Connect Google Drive first. A link on its own doesn’t give Memoryflix access to a file.');
        await pick({ kind: 'file', id: info.id });
      } else if (info.kind === 'folder' && info.id) {
        if (!status?.connected) return setError('Connect Google Drive first, then choose the photos and videos in that folder.');
        await pick({ kind: 'folder', id: info.id });
      } else {
        setError(info.message);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.googleDisconnect();
      setConfirmDisconnect(false);
      setNotice('Google Drive disconnected. Memoryflix no longer has access, and memories you imported stay in your library.');
      onStatusChange();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode;
  if (!status) {
    body = <div className="skeleton h-28 w-full" />;
  } else if (!status.configured) {
    body = (
      <div className="text-sm text-white/60">
        <p>Google Drive import isn’t set up on this server yet.</p>
        <p className="mt-2 text-xs text-white/40">
          Whoever runs Memoryflix needs to create Google credentials (see docs/GOOGLE_DRIVE_SETUP.md).
          Missing settings: {status.missing.join(', ')}.
        </p>
      </div>
    );
  } else if (!status.connected) {
    body = (
      <div>
        <ul className="mb-5 space-y-2 text-sm text-white/65">
          <li>You choose each photo or video in Google’s own file chooser.</li>
          <li>Memoryflix can open only the files you choose. It can’t browse or change the rest of your Drive.</li>
          <li>You sign in on Google’s page. Memoryflix never sees your password.</li>
          <li>Google will also share your email address, so you can see which account is connected.</li>
        </ul>
        <button
          onClick={connect}
          className="focus-ring w-full rounded-md bg-white px-6 py-2.5 text-sm font-bold text-black transition hover:bg-white/85"
        >
          Connect Google Drive
        </button>
      </div>
    );
  } else {
    body = (
      <div>
        <p className="mb-4 text-sm text-white/60">
          Connected as <span className="text-white/90">{status.email ?? 'your Google account'}</span>
        </p>
        <button
          onClick={() => pick({ kind: 'browse' })}
          disabled={busy}
          className="focus-ring w-full rounded-md bg-white px-6 py-2.5 text-sm font-bold text-black transition hover:bg-white/85 disabled:opacity-40"
        >
          {busy ? 'Opening Google Drive…' : 'Choose from Google Drive'}
        </button>
        <p className="mt-2 text-xs text-white/40">
          Open a folder to pick the photos and videos inside it, up to {MAX_ITEMS} at a time.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center gap-2.5 text-white/80">
        <DriveGlyph />
        <h2 className="text-base font-semibold">Google Drive</h2>
      </div>

      {body}

      {status?.configured && (
        <form onSubmit={openLink} className="mt-6 border-t border-white/5 pt-5">
          <label htmlFor="drive-link" className="mb-1.5 block text-sm text-white/70">
            Import from a Drive link
          </label>
          <div className="flex gap-2">
            <input
              id="drive-link"
              type="url"
              inputMode="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://drive.google.com/…"
              className="focus-ring min-w-0 flex-1 rounded-md border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/25"
            />
            <button
              type="submit"
              disabled={busy || !link.trim()}
              className="focus-ring shrink-0 rounded-md bg-white/15 px-4 py-2 text-sm font-semibold transition hover:bg-white/25 disabled:opacity-40"
            >
              Open
            </button>
          </div>
          <p className="mt-1.5 text-xs text-white/35">
            Works with file and shared-folder links you can open yourself. You’ll confirm the files in Google’s chooser.
          </p>
        </form>
      )}

      {notice && (
        <p className="mt-4 text-sm text-emerald-200/90" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100" role="alert">
          {error}
        </p>
      )}

      {status?.connected && (
        <div className="mt-auto pt-6 text-xs">
          {confirmDisconnect ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-white/60">Disconnect Google Drive? Imported memories stay.</span>
              <button
                onClick={disconnect}
                disabled={busy}
                className="focus-ring rounded bg-red-500/80 px-3 py-1 font-semibold text-white hover:bg-red-500"
              >
                Disconnect
              </button>
              <button onClick={() => setConfirmDisconnect(false)} className="focus-ring rounded px-2 py-1 text-white/60 hover:text-white">
                Keep connected
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDisconnect(true)}
              className="focus-ring rounded text-white/40 underline decoration-white/20 underline-offset-4 hover:text-white/80"
            >
              Disconnect Google Drive
            </button>
          )}
        </div>
      )}
    </div>
  );
}
